// Scherm 5 — de workspace. W9-besluit B5: "de workspace is de landingspagina van
// de rol", en de owner koos bij de review van sessie 1: alle drie de rollen, met
// eerlijke lege blokken.
//
// Vorm uit V2_frontend.jsx (r. 3708 e.v.): kolom van 780px, welkomstregel van
// 22/700 met een subregel eronder, daarna secties met een kop van 14/700 en een
// uitleg van 12px. De chatbalk is de eerste sectie, de gebeurtenissen zijn de
// derde — precies de volgorde die daar staat.
//
// WAAROM ER EEN ROLWISSEL IN ZIT. De persoonlijke inlog komt in sessie 4, en de
// enige gebruiker die vandaag bestaat is de beheerder — die draagt geen
// inhoudelijke rol (gemeten: rol = null). Zonder wisselaar zou dit scherm dus
// voor iedereen leeg zijn en viel er niets te beoordelen. De rollen komen uit
// /api/meta (config/rollen.yaml), nooit uit een lijst in deze code: één as, één
// woordenlijst, één plek (backlog 30).
//
// WAT ER BEWUST NIET IN ZIT. Rolafhankelijke inhoud — feeds, duiding,
// contentformats — is V2 (W4/K3, de redactielaag) en staat hier als blok met
// zijn reden. Een onderwerp aanvragen kan hier nog niet (dat formulier hoort
// bij W9 deel B). Méér krediet aanvragen wel, sinds W6 ronde 2 (V2-PRD §11.1):
// dat verzoek landt op de beheerwachtrij, waar de beheerder ja of nee zegt.

import { useEffect, useMemo, useState } from "react";

import {
  EditieStroom,
  type SubsidieItem,
  type VasteRegeling,
} from "../components/EditieStroom";
import { useFunding } from "../lib/funding";
import type { KaartOpenen } from "../components/EditieKaart";
import { ComingSoon, EmptyReason, LoadError } from "../components/Empty";
import { EventFeed } from "../components/EventFeed";
import type { Aanbouw } from "../components/InAanbouw";
import { AANBOUW, AanbouwKnop, InAanbouwPaneel } from "../components/InAanbouw";
import { Card, SectionLabel } from "../components/ui";
import type { MetaResponse } from "../contracts";
import { useMeta } from "../lib/meta";
import { PLAN_ROLLEN, magPlan, registerSectoren, useMij, type MijStand } from "../lib/mij";
import { useActieveSector } from "../lib/sectorkeuze";
import { ApiError, api } from "../lib/api";
import { useEdities, useEditie } from "../lib/redactie";
import { useResource } from "../lib/useResource";
import { color, font, radius, size, weight } from "../tokens";
import { kredietLabel } from "./beheer/woorden";

type Rol = NonNullable<MetaResponse["rollen"]>[number];

/** Het rol-eigen blok: wat deze rol straks krijgt, en waarom het er nog niet is. */
const ROL_BLOK: Record<string, { titel: string; reden: string }> = {
  accountmanagement: {
    titel: "Mijn bedrijven",
    reden:
      "De bedrijvenlaan is ontworpen (de Bedrijfsspiegel uit W4) maar niet gebouwd: er is " +
      "geen bedrijvenregister, geen vacature-extractie en geen skill-gap-analyse. Zodra die " +
      "laan draait staat je portefeuille hier, met per bedrijf de gevraagde en de aanwezige " +
      "vaardigheden naast elkaar.",
  },
  directie: {
    titel: "Marktbeweging en duiding",
    reden:
      "De duiding per rol en propositie komt uit een redactielaag die nog niet draait, en de " +
      "verschuivingen (versnelt, nu bevestigd, stil geworden) hebben meer metingen " +
      "nodig om iets tegen af te zetten. Wat er vandaag wél is, staat in de gebeurtenissen " +
      "hierboven en op het trendscherm.",
  },
  marketing: {
    titel: "Contentformats en de index",
    reden:
      "De formats (Skill van de maand, Mover & Shaker, sectorrapport) komen uit diezelfde " +
      "redactielaag. De kwartaalindex vraagt bovendien meerdere maanden aan metingen én de " +
      "onderwijsaanbod-kant, en die wordt vandaag nergens gemeten.",
  },
};

/**
 * De ingang naar het Plan-tabblad (voorproef 30-09), op de plek waar
 * "Mijn bedrijven" als binnenkort stond. De bedrijvenlaan is er nog steeds
 * niet; het marktbewerkingsplan wel, als voorproef.
 */
function PlanKaart({ onOpen, sectorLabel }: { onOpen: () => void; sectorLabel?: string | null }) {
  return (
    <button
      onClick={onOpen}
      style={{
        width: "100%",
        textAlign: "left",
        background: color.surface,
        border: `1px solid ${color.line}`,
        borderRadius: radius.block,
        padding: "16px 18px",
        cursor: "pointer",
        fontFamily: "inherit",
        display: "flex",
        alignItems: "center",
        gap: 14,
      }}
    >
      <span style={{ flex: 1 }}>
        <span style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <span style={{ fontSize: size.lead, fontWeight: weight.bold, color: color.ink }}>
            {/* Pilot 26-10: de naam van de eigen propositie, nooit een vaste "ICT". */}
            {sectorLabel ? `Marktbewerkingsplan ${sectorLabel}` : "Marktbewerkingsplan"}
          </span>
          <span
            style={{
              fontSize: 10.5,
              color: color.accentAmber,
              background: color.accentAmberBg,
              border: `1px solid ${color.accentAmber}33`,
              borderRadius: radius.pill,
              padding: "2px 8px",
            }}
          >
            voorproef
          </span>
        </span>
        <span style={{ display: "block", fontSize: size.meta, color: color.soft, lineHeight: 1.5 }}>
          Hoofdstuk 1 met de macrogrond, de regio, de trends en de opleidingskansen. Bronnen en
          trends komen live uit de kennisbank.
        </span>
      </span>
      <span style={{ color: color.faint, fontSize: 18 }}>→</span>
    </button>
  );
}

function Sectie({
  titel,
  uitleg,
  children,
}: {
  titel: string;
  uitleg?: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{ fontSize: size.body, fontWeight: weight.bold, color: color.ink }}>
        {titel}
      </div>
      {uitleg && (
        <div style={{ fontSize: 12, color: color.faint, marginTop: 4, marginBottom: 12 }}>
          {uitleg}
        </div>
      )}
      {!uitleg && <div style={{ height: 12 }} />}
      {children}
    </div>
  );
}

/** Wat de editie deze maand opleverde, in één zin (V2 r. 3711). */
type Maandzin = { inzichten: number; maand: string } | null;

/** De welkomstregel. Zonder inlog verzinnen we geen naam. */
function Welkom({
  stand,
  rol,
  maandzin,
}: {
  stand: MijStand;
  rol: Rol | null;
  maandzin: Maandzin;
}) {
  const naam = stand.stand === "ingelogd" ? stand.mij.naam : null;
  const eigenRol = stand.stand === "ingelogd" ? stand.mij.rol : null;
  const voorbeeld = rol !== null && rol.key !== eigenRol;

  return (
    <div style={{ marginBottom: 28 }}>
      <div
        style={{
          fontSize: 22,
          fontWeight: weight.bold,
          color: color.ink,
          marginBottom: 5,
        }}
      >
        {naam ? `Welkom terug, ${naam}` : "Workspace"}
      </div>
      {/* De maandzin uit V2: hoeveel inzichten er deze ronde liggen. Het
          getal komt uit de editie zelf, niet uit een aparte telling — anders
          staan er twee waarheden op één scherm. */}
      {maandzin && (
        <div
          style={{
            fontSize: size.body,
            color: color.body,
            lineHeight: 1.6,
            marginBottom: 4,
          }}
        >
          <b style={{ color: color.ink, fontWeight: weight.medium }}>
            {maandzin.inzichten} inzichten
          </b>{" "}
          in deze editie.
        </div>
      )}
      <div style={{ fontSize: size.body, color: color.soft, lineHeight: 1.6 }}>
        {stand.stand === "laden" && "Bezig met ophalen wie je bent…"}
        {stand.stand === "geen_inlog" && (
          <>
            Nog niemand ingelogd. De schil zet je normaal op het inlogscherm; kom je
            hier tóch, dan draait deze kopie zonder inlog en kun je de drie
            workspaces hieronder als voorbeeld bekijken.
          </>
        )}
        {stand.stand === "geen_toegang" && stand.reden}
        {stand.stand === "ingelogd" && (
          <>
            {stand.mij.rolLabel ? (
              <>
                Je rol is <b style={{ color: color.ink, fontWeight: weight.medium }}>
                  {stand.mij.rolLabel}
                </b>
                {stand.mij.propositieLabel ? ` · ${stand.mij.propositieLabel}` : ""}
                {stand.mij.isAdmin ? " · beheerder" : ""}.
              </>
            ) : (
              <>
                Je bent beheerder zonder inhoudelijke rol. Een workspace hoort bij
                een rol, dus die van jou bestaat niet — hieronder staan de drie die
                er wél zijn, als voorbeeld.
              </>
            )}
          </>
        )}
      </div>
      {voorbeeld && stand.stand !== "laden" && (
        <div
          style={{
            marginTop: 10,
            display: "inline-block",
            fontSize: 12,
            color: color.accentAmber,
            background: color.accentAmberBg,
            border: `1px solid ${color.accentAmber}33`,
            borderRadius: radius.pill,
            padding: "4px 12px",
          }}
        >
          {stand.stand === "ingelogd"
            ? `Je kijkt mee in de workspace van ${rol.label} — dit is niet jouw eigen rol`
            : `Voorbeeld: zo ziet de workspace van ${rol.label} eruit`}
        </div>
      )}
    </div>
  );
}

function RolWissel({
  rollen,
  actief,
  eigenRol,
  onKies,
}: {
  rollen: Rol[];
  actief: string | null;
  eigenRol: string | null;
  onKies: (key: string) => void;
}) {
  return (
    <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
      {rollen.map((r) => {
        const aan = r.key === actief;
        return (
          <button
            key={r.key}
            onClick={() => onKies(r.key)}
            style={{
              background: aan ? color.ink : color.surface,
              color: aan ? color.surface : color.soft,
              border: `1px solid ${aan ? color.ink : color.border}`,
              borderRadius: radius.pill,
              padding: "5px 13px",
              fontSize: 12,
              fontWeight: weight.regular,
              fontFamily: "inherit",
              cursor: "pointer",
              transition: "all 0.12s",
            }}
            onMouseEnter={(e) => {
              if (aan) return;
              e.currentTarget.style.borderColor = color.disabled;
              e.currentTarget.style.color = color.body;
            }}
            onMouseLeave={(e) => {
              if (aan) return;
              e.currentTarget.style.borderColor = color.border;
              e.currentTarget.style.color = color.soft;
            }}
          >
            {r.label}
            {r.key === eigenRol ? " · jouw rol" : ""}
          </button>
        );
      })}
    </div>
  );
}

// De woorden per kredietsoort staan sinds review ronde 2 in beheer/woorden.ts:
// de wachtrij noemt dezelfde soort ("Vraagt +20 gesprekken"), en twee lijsten
// zouden uit elkaar lopen.

function Krediet({
  rol,
  stand,
  isEigenRol,
}: {
  rol: Rol;
  stand: MijStand;
  isEigenRol: boolean;
}) {
  const soorten = Object.keys(rol.krediet ?? {});
  if (soorten.length === 0) {
    return <EmptyReason reason="deze rol heeft geen krediet nodig" />;
  }

  const eigen = stand.stand === "ingelogd" && isEigenRol ? stand.mij.krediet : null;
  // "concept" komt alleen uit /api/mij: de rolmaxima in /api/meta dragen hem
  // niet. Wie in een andere rol meekijkt ziet het getal dus zonder die vlag.
  const heeftConcept = soorten.some((s) => eigen?.[s]?.concept === true);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {soorten.map((soort) => {
        // In je eigen rol geldt het maximum uit /api/mij: daar zit een
        // persoonlijk maximum al in, en anders de rolstandaard die de beheerder
        // vaststelde. Wie in een andere rol meekijkt, ziet de rolwaarde uit
        // /api/meta (ook die draagt de vastgestelde standaard).
        const max = eigen?.[soort]?.maximum ?? (rol.krediet ?? {})[soort] ?? 0;
        const gebruikt = eigen?.[soort]?.gebruikt;
        const deel = max > 0 && typeof gebruikt === "number" ? gebruikt / max : 0;
        return (
          <div key={soort}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                marginBottom: 5,
              }}
            >
              <span style={{ fontSize: size.meta, color: color.body }}>
                {kredietLabel(soort)}
                {eigen?.[soort]?.concept === true && (
                  // W6: het maximum is een startwaarde uit config/rollen.yaml,
                  // nog geen owner-getal. Zonder deze vlag leest "100" als besluit.
                  <span
                    title="Dit maximum is een startwaarde, nog geen vastgesteld getal."
                    style={{
                      marginLeft: 6,
                      fontSize: 10,
                      fontWeight: weight.medium,
                      color: color.accentAmber,
                      background: color.accentAmberBg,
                      border: `1px solid ${color.accentAmber}33`,
                      borderRadius: radius.pill,
                      padding: "1px 7px",
                      letterSpacing: "0.04em",
                      textTransform: "uppercase",
                    }}
                  >
                    concept
                  </span>
                )}
              </span>
              <span style={{ fontSize: size.meta, color: color.faint }}>
                {typeof gebruikt === "number"
                  ? `${gebruikt} van de ${max} deze maand`
                  : `maximaal ${max} per maand`}
              </span>
            </div>
            <div style={{ height: 3, background: color.lineSoft, borderRadius: 99 }}>
              <div
                style={{
                  height: "100%",
                  width: `${Math.min(100, deel * 100)}%`,
                  background: color.ink,
                  borderRadius: 99,
                }}
              />
            </div>
            {/* Alleen in je eigen workspace: aanvragen doe je voor jezelf, niet
                voor de rol waarin je meekijkt. */}
            {eigen && <MeerAanvragen soort={soort} label={kredietLabel(soort)} />}
          </div>
        );
      })}
      <p style={{ fontSize: 12, color: color.faint, lineHeight: 1.55 }}>
        {typeof eigen?.[soorten[0]!]?.gebruikt === "number"
          ? "Verbruik wordt geteld, niet bijgehouden in een teller: " +
            (soorten.includes("gesprek")
              ? "onderwerpen uit goedgekeurde verzoeken, gesprekken uit beantwoorde chatvragen. "
              : "uit goedgekeurde verzoeken. ") +
            "Elk verzoek gaat langs de beheerder, ook binnen het krediet."
          : "Dit is het maximum van deze rol. Het verbruik is persoonlijk en verschijnt zodra " +
            "je met je eigen account bent ingelogd."}
      </p>
      {heeftConcept && (
        <p style={{ fontSize: 12, color: color.faint, lineHeight: 1.55 }}>
          Een maximum met "concept" is een startwaarde om het verbruik tegen af te zetten,
          nog geen vastgesteld getal.
        </p>
      )}
      {!eigen && (
        <p style={{ fontSize: 12, color: color.faint, lineHeight: 1.55 }}>
          Meer krediet aanvragen kan alleen in je eigen workspace, als je met je eigen
          account bent ingelogd.
        </p>
      )}
    </div>
  );
}

/**
 * "Meer aanvragen" onder één kredietsoort (W6 ronde 2, V2-PRD §11.1). Een klein
 * formulier op de plek zelf, geen apart scherm: één getal, één knop. Het
 * verzoek gaat naar de beheerwachtrij; wat wel en niet mag (soort, 1 tot 1000,
 * al open) beslist de server, en zijn Nederlandse reden staat hier letterlijk.
 */
function MeerAanvragen({ soort, label }: { soort: string; label: string }) {
  const [open, setOpen] = useState(false);
  const [aantal, setAantal] = useState("20");
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const [klaar, setKlaar] = useState(false);

  async function vraag() {
    setBezig(true);
    setFout(null);
    try {
      await api.dienVerzoekIn({
        soort: "krediet",
        titel: `Meer krediet: ${label}`,
        payload: { soort, aantal: Number(aantal) },
      });
      setKlaar(true);
      setOpen(false);
    } catch (err) {
      setFout(
        err instanceof ApiError
          ? err.status === 0
            ? err.reason
            : err.message || err.reason
          : "Het verzoek kon niet worden verstuurd.",
      );
    } finally {
      setBezig(false);
    }
  }

  const linkKnop = {
    background: "none",
    border: "none",
    padding: 0,
    marginTop: 6,
    font: "inherit",
    fontSize: 12,
    color: color.accentBlue,
    cursor: "pointer",
  } as const;

  if (klaar) {
    return (
      <p style={{ fontSize: 12, color: color.accentGreen, marginTop: 6 }}>
        Aangevraagd — de beheerder beslist.
      </p>
    );
  }

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} style={linkKnop}>
        + Meer aanvragen
      </button>
    );
  }

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <label style={{ fontSize: 12, color: color.soft, display: "flex", alignItems: "center", gap: 6 }}>
          Hoeveel extra?
          <input
            type="number"
            min={1}
            step={1}
            inputMode="numeric"
            value={aantal}
            onChange={(e) => setAantal(e.target.value)}
            style={{
              width: 72,
              padding: "5px 8px",
              fontSize: size.meta,
              fontFamily: "inherit",
              color: color.ink,
              border: `1px solid ${color.border}`,
              borderRadius: radius.input,
            }}
          />
        </label>
        <button
          type="button"
          onClick={() => void vraag()}
          disabled={bezig}
          style={{
            background: bezig ? color.disabled : color.ink,
            color: color.surface,
            border: "none",
            borderRadius: radius.input,
            padding: "6px 12px",
            fontSize: 12,
            fontWeight: weight.medium,
            fontFamily: "inherit",
            cursor: bezig ? "default" : "pointer",
          }}
        >
          Aanvragen
        </button>
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            setFout(null);
          }}
          style={{ ...linkKnop, marginTop: 0, color: color.faint }}
        >
          Annuleer
        </button>
      </div>
      {fout && (
        <p style={{ fontSize: 12, color: color.accentRed, marginTop: 6, lineHeight: 1.5 }}>{fout}</p>
      )}
    </div>
  );
}

function Propositie({ rol, stand, isEigenRol, meta }: {
  rol: Rol;
  stand: MijStand;
  isEigenRol: boolean;
  meta: MetaResponse | null;
}) {
  const sectoren = meta?.sectors ?? [];
  const label = (key: string) => sectoren.find((s) => s.key === key)?.label ?? key;
  const isActief = (key: string) => sectoren.find((s) => s.key === key)?.active === true;

  if (stand.stand === "ingelogd" && isEigenRol) {
    const mij = stand.mij;
    if (!mij.propositie) {
      return (
        <EmptyReason
          reason={
            rol.propositieVerplicht
              ? "nog geen propositie toegekend — de beheerder doet dat bij de uitnodiging"
              : "deze rol werkt cross-sector en hangt bewust aan geen enkele school"
          }
        />
      );
    }
    return (
      <div>
        <div style={{ fontSize: size.body, color: color.ink, marginBottom: 8 }}>
          {mij.propositieLabel ?? mij.propositie}
        </div>
        {mij.sectoren.length === 0 ? (
          <EmptyReason reason="deze school raakt geen van de zestien marktsectoren" />
        ) : (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {mij.sectoren.map((s) => (
              <span
                key={s}
                title={
                  isActief(s)
                    ? "Deze sector is gemeten: er staat een corpus onder."
                    : "Deze sector is voorbereid maar heeft nog geen corpus."
                }
                style={{
                  fontSize: 12,
                  color: isActief(s) ? color.body : color.faint,
                  background: isActief(s) ? color.lineFaint : color.surfaceQuote,
                  border: `1px solid ${color.border}`,
                  borderRadius: radius.pill,
                  padding: "4px 11px",
                }}
              >
                {label(s)}
                {isActief(s) ? "" : " — nog geen corpus"}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <EmptyReason
      reason={
        rol.propositieVerplicht
          ? "een propositie hangt aan een persoon, niet aan een rol — hij verschijnt zodra je bent ingelogd"
          : "deze rol werkt cross-sector en draagt bewust geen propositie"
      }
    />
  );
}

/** De maandredactie: de editie van deze rol, of de eerlijke reden dat hij er niet is. */
function Maandredactie({
  rol,
  eigenPropositie,
  onOpen,
  onMaandzin,
}: {
  rol: string;
  eigenPropositie: string | null;
  onOpen?: KaartOpenen;
  onMaandzin?: (z: Maandzin) => void;
}) {
  const edities = useEdities();
  const [maand, setMaand] = useState<string | null>(null);

  // De subsidiesignalen uit de gebeurtenis-feed. Ze horen bij de type-pill
  // "Subsidies" (V2-indeling per soort): vensters die openen of sluiten en
  // nieuwe regelingen in het register. Eén extra aanroep, met een eigen
  // filter, zodat de 3.563 trend-events hem niet wegdrukken.
  // ADR 2026-10-23-ict-los-platform: de bedrijfstakken van de propositie van
  // de gebruiker; zolang alleen ICT zichtbaar is ICT, zoals het was.
  const mijStand = useMij();
  const regSectoren = registerSectoren(mijStand.stand === "ingelogd" ? mijStand.mij : null);
  const fundingFeed = useResource(
    () => api.feed({ limit: 25, type: ["funding_alert"], sector: regSectoren[0] }),
    [regSectoren[0]],
  );
  const subsidies: SubsidieItem[] = useMemo(
    () =>
      (fundingFeed.data?.items ?? []).map((i) => ({
        id: i.id,
        naam: i.subject?.name ?? "",
        datum: i.date ?? null,
        regelingId: i.subject?.id ?? null,
      })),
    [fundingFeed.data],
  );

  // B9 (feedbackronde 27-08): naast de signalen ook de vaste voorraad — de
  // sector-relevante regelingen uit het register (ICT-specifiek plus
  // sectorbreed; de or-clausule van de funding-lijn telt sectorbreed mee).
  // "Het blijft altijd relevant": de pil toont alles, de gemengde stroom
  // alleen wat beweegt.
  const register = useFunding({ sector: regSectoren, sorteer: "venster", pageSize: 50 });

  // B6 golf 1: de gemeten feedposten — versnellers, cijferkaarten,
  // vooruitblik, opkomende termen. $0 per ronde; een lege response betekent
  // gewoon geen kaarten.
  // Per sector (B1): de posten van de sector waar de schermen op staan.
  const postenSector = useActieveSector();
  const gemetenPosten = useResource(() => api.posten(postenSector), [postenSector]);
  const regelingen: VasteRegeling[] = useMemo(
    () =>
      (register.data?.items ?? []).map((r) => ({
        id: r.id,
        naam: r.naam,
        verstrekker: r.verstrekker ?? null,
        status: r.status ?? null,
      })),
    [register.data],
  );

  // Welke editie tonen we? Bij een ingelogde gebruiker die van zijn eigen
  // propositie is; anders de eerste die er is, mét de melding dat het een
  // voorbeeld is. Een propositie verzinnen zou hier een editie tonen die niet
  // van deze lezer is zonder dat te zeggen.
  //
  // Pilot gereed 26-10 (baan 1C, review): het voorbeeld komt alleen uit de
  // propositie van de sector waar de schermen op staan, nooit uit "de eerste
  // die er is" — anders zag een E&O-gebruiker in een andere rol, of zolang
  // E&O nog geen editie heeft, de ICT-editie. Met alleen ICT zichtbaar is dat
  // precies de oude keuze (de ICT-editie, met de melding "Voorbeeld").
  const meta = useMeta();
  const schermSector = postenSector ?? meta?.zichtbareSectoren?.[0] ?? "ict";
  const sectorPropositie =
    meta?.proposities?.find((p) => p.sector === schermSector)?.key ?? (schermSector === "ict" ? "ict" : null);
  const beschikbaar = useMemo(
    () => (edities.data?.edities ?? []).filter((e) => e.rol === rol),
    [edities.data, rol],
  );
  const gekozen = useMemo(() => {
    const eigen = beschikbaar.find((e) => e.propositie === eigenPropositie);
    if (eigen) return eigen;
    // Een eigen propositie zonder editie: de lege staat, geen andermans editie.
    if (eigenPropositie !== null) return null;
    return beschikbaar.find((e) => e.propositie === sectorPropositie) ?? null;
  }, [beschikbaar, eigenPropositie, sectorPropositie]);

  const editie = useEditie(gekozen?.propositie ?? null, rol, maand);

  // De maandzin in de welkomstregel komt uit de editie zelf. Melden gebeurt in
  // een effect en niet tijdens het renderen: de telling hoort bij de DATA die
  // geladen is, en een setState in de renderfase van een ander component is
  // precies de lus die React verbiedt.
  const kaartAantal = editie.data?.kaarten.length ?? null;
  const editieMaand = editie.data?.maand ?? null;
  useEffect(() => {
    if (kaartAantal === null || editieMaand === null) return;
    onMaandzin?.({ inzichten: kaartAantal + subsidies.length, maand: editieMaand });
  }, [kaartAantal, editieMaand, subsidies.length, onMaandzin]);

  if (edities.error) return <LoadError reason={edities.error} />;
  if (edities.loading && !edities.data) {
    return <EmptyReason reason="bezig met ophalen…" />;
  }
  if (gekozen === null) {
    return (
      <ComingSoon
        title="De maandredactie"
        reason={
          "Er is voor deze rol nog geen editie gemaakt. De redactie draait per propositie " +
          "en per maand; zodra er één gedraaid is verschijnt hij hier, met de maandkiezer " +
          "erbij om terug te bladeren."
        }
      />
    );
  }
  if (editie.error) return <LoadError reason={editie.error} />;
  if (!editie.data) return <EmptyReason reason="bezig met ophalen…" />;

  const maanden = [...new Set(
    beschikbaar
      .filter((e) => e.propositie === gekozen.propositie)
      .map((e) => e.maand),
  )];
  const isVoorbeeld = eigenPropositie !== null && gekozen.propositie !== eigenPropositie;

  return (
    <div style={{ opacity: editie.stale ? 0.6 : 1 }}>
      {(isVoorbeeld || eigenPropositie === null) && (
        <div
          style={{
            marginBottom: 12,
            fontSize: 12,
            color: color.accentAmber,
            background: color.accentAmberBg,
            border: `1px solid ${color.accentAmber}33`,
            borderRadius: radius.pill,
            padding: "4px 12px",
            display: "inline-block",
          }}
        >
          Voorbeeld: de editie van {editie.data.propositieLabel ?? gekozen.propositie}
        </div>
      )}
      <div
        style={{
          marginBottom: 12,
          fontSize: 12,
          color: color.soft,
          background: color.surface,
          border: `1px solid ${color.line}`,
          borderRadius: radius.pill,
          padding: "4px 12px",
          display: "inline-block",
        }}
      >
        In ontwikkeling
      </div>
      <EditieStroom
        editie={editie.data}
        maanden={maanden}
        gekozenMaand={editie.data.maand}
        onKiesMaand={setMaand}
        onOpen={onOpen}
        // Zonder deze prop waren [[trend:…]]-verwijzingen in kaartteksten
        // dode tekst: EditieStroom gaf hem door, maar kreeg hem hier nooit
        // (feed→chat-gat 5, ontwerp 26-08).
        onVerwijzing={(soort, id) =>
          onOpen?.({
            soort: "paneel",
            tab: soort === "bron" ? "sources" : soort === "skill" ? "skills" : "trends",
            id,
          })
        }
        subsidies={subsidies}
        regelingen={regelingen}
        posten={gemetenPosten.data ?? null}
      />
    </div>
  );
}

export function WorkspacePage({
  onChat,
  onOpen,
  rol: rolVanBuiten,
  onRol,
}: {
  onChat: () => void;
  onOpen?: KaartOpenen;
  /**
   * De rolkeuze, gedeeld met de rest van het scherm. V2 zet `roleKey` op
   * app-niveau en laat hem óók de chat aansturen (V2_frontend.jsx r. 3583,
   * 3921): kies je hier een rol, dan wisselen de openingsvragen van de chat
   * mee. Ontbreken deze props, dan houdt dit scherm zijn eigen keuze bij en
   * verandert er niets — de wisselaar werkt dan zoals hij altijd werkte.
   */
  rol?: string | null;
  onRol?: (rol: string) => void;
}) {
  const stand = useMij();
  const meta = useMeta();
  const [eigenKeuze, setEigenKeuze] = useState<string | null>(null);
  const [maandzin, setMaandzin] = useState<Maandzin>(null);
  const [alleGebeurtenissen, setAlleGebeurtenissen] = useState(false);
  const [aanbouw, setAanbouw] = useState<Aanbouw | null>(null);
  const gekozenRol = rolVanBuiten !== undefined ? rolVanBuiten : eigenKeuze;
  const setGekozenRol = (r: string) => {
    setEigenKeuze(r);
    onRol?.(r);
  };

  const rollen = meta?.rollen ?? [];
  const eigenRol = stand.stand === "ingelogd" ? (stand.mij.rol ?? null) : null;
  const actieveKey = gekozenRol ?? eigenRol ?? rollen[0]?.key ?? null;
  const rol = rollen.find((r) => r.key === actieveKey) ?? null;
  const isEigenRol = rol !== null && rol.key === eigenRol;

  return (
    <div style={{ maxWidth: 780, margin: "0 auto", fontFamily: font }}>
      <Welkom stand={stand} rol={rol} maandzin={maandzin} />

      {stand.stand === "fout" && <LoadError reason={stand.reden} />}

      {rollen.length > 0 ? (
        <Sectie
          titel="Workspace van"
          uitleg={
            "Elke rol landt op zijn eigen workspace. De rollen komen uit de configuratie " +
            "(rollen.yaml); LLO staat daar op inactief tot de crebo-koppeling er is."
          }
        >
          <RolWissel
            rollen={rollen}
            actief={actieveKey}
            eigenRol={eigenRol}
            onKies={setGekozenRol}
          />
        </Sectie>
      ) : (
        <Sectie titel="Workspace van">
          <EmptyReason reason="de rollenlijst kon niet worden opgehaald" />
        </Sectie>
      )}

      <Sectie
        titel="Chat met de bronnen"
        uitleg="Stel een vraag over trends, vaardigheden of documenten; elk antwoord draagt zijn bronnen."
      >
        <div
          onClick={onChat}
          style={{
            background: color.surface,
            border: `1.5px solid ${color.border}`,
            borderRadius: 14,
            padding: "16px 18px",
            display: "flex",
            alignItems: "center",
            gap: 12,
            cursor: "pointer",
            transition: "border-color 0.15s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = color.disabled;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = color.border;
          }}
        >
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: 8,
              background: color.ink,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path d="M8 2v12M2 8h12" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </div>
          <span style={{ fontSize: size.body, color: color.faint, flex: 1 }}>
            Stel een vraag aan de bronnen…
          </span>
        </div>
      </Sectie>

      {/* ── SKILLS: de vier ingangen uit barometer-workspace.jsx ──────────
          Twee ervan kunnen vandaag echt werk doen (de funding-laan draait, en
          het quick-win-filter bestaat op /api/skills); twee wachten op de
          bedrijvenlaan en zeggen dat bij een klik. */}
      <Sectie
        titel="Skills"
        uitleg="Vier ingangen naar de vaardigheden en het geld dat erbij hoort."
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))",
            gap: 8,
          }}
        >
          <AanbouwKnop tekst="Onderzoek bedrijf" ico="🔍" onKlik={() => setAanbouw(AANBOUW.onderzoek!)} />
          <AanbouwKnop
            tekst="Subsidies zoeken"
            ico="€"
            onKlik={() => onOpen?.({ soort: "tab", tab: "funding" })}
          />
          <AanbouwKnop tekst="Vergelijk" ico="⇄" onKlik={() => setAanbouw(AANBOUW.vergelijk!)} />
          <AanbouwKnop
            tekst="Quick wins"
            ico="⚡"
            onKlik={() => onOpen?.({ soort: "tab", tab: "skills", filter: { quickWin: true } })}
          />
        </div>
      </Sectie>

      {/* ── MIJN BEDRIJVEN ──────────────────────────────────────────────── */}
      <Sectie
        titel="Mijn bedrijven"
        uitleg="Klik op een afgerond bedrijf voor het skill-profiel en de skill gap."
      >
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
          <AanbouwKnop tekst="+ Nieuw onderzoek" onKlik={() => setAanbouw(AANBOUW.nieuwOnderzoek!)} />
        </div>
        <button
          onClick={() => setAanbouw(AANBOUW.bedrijven!)}
          style={{
            width: "100%",
            border: `1px dashed ${color.border}`,
            borderRadius: radius.block,
            padding: "18px 14px",
            background: color.surface,
            cursor: "pointer",
            fontFamily: "inherit",
            textAlign: "center",
          }}
        >
          <span
            style={{
              fontSize: 10.5,
              color: color.accentAmber,
              background: color.accentAmberBg,
              border: `1px solid ${color.accentAmber}33`,
              borderRadius: radius.pill,
              padding: "2.5px 9px",
            }}
          >
            in aanbouw
          </span>
          <span
            style={{
              display: "block",
              fontSize: size.label,
              color: color.faint,
              marginTop: 7,
              lineHeight: 1.5,
            }}
          >
            Klik voor wat dit blok gaat doen en wat ervoor nodig is.
          </span>
        </button>
      </Sectie>

      {rol && (
        <Sectie
          titel="De maandredactie"
          uitleg={
            "Wat er uit de bronnen komt, in kaarten. Klik een kaart open voor " +
            "het bewijs eronder, en door voor het onderwerp zelf."
          }
        >
          <Maandredactie
            rol={rol.key}
            eigenPropositie={
              stand.stand === "ingelogd" && isEigenRol ? (stand.mij.propositie ?? null) : null
            }
            onOpen={onOpen}
            onMaandzin={setMaandzin}
          />
        </Sectie>
      )}

      {/* ── JE VOLGT ───────────────────────────────────────────────────── */}
      <div
        style={{
          marginBottom: 32,
          display: "flex",
          alignItems: "center",
          gap: 7,
          flexWrap: "wrap",
        }}
      >
        <span style={{ fontSize: 12, color: color.faint }}>Je volgt:</span>
        <button
          onClick={() => setAanbouw(AANBOUW.onderwerp!)}
          style={{
            border: `1px dashed ${color.border}`,
            background: color.surface,
            borderRadius: radius.pill,
            padding: "5px 13px",
            fontSize: 12,
            color: color.faint,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          + Onderwerp volgen
        </button>
      </div>

      {/* De kale gebeurtenislijst staat INGEKLAPT onder de redactie
          (owner-opname jfbgusr4f1nl, 0:10: "de feed werkt zo niet ... het is
          super complex"). Hij verdwijnt niet — 5.329 gebeurtenissen zijn de
          onderbouwing onder de editie — maar hij is niet meer het eerste wat
          je ziet. */}
      <div style={{ marginBottom: 32 }}>
        <button
          onClick={() => setAlleGebeurtenissen((a) => !a)}
          aria-expanded={alleGebeurtenissen}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: "none",
            border: "none",
            padding: 0,
            font: "inherit",
            fontSize: size.body,
            fontWeight: weight.bold,
            color: color.ink,
            cursor: "pointer",
          }}
        >
          <span style={{ color: color.disabled, fontSize: 12 }}>
            {alleGebeurtenissen ? "▾" : "▸"}
          </span>
          Alle gebeurtenissen
        </button>
        <div style={{ fontSize: 12, color: color.faint, marginTop: 4 }}>
          De kale lijst onder de redactie: nieuwe trends, vaardigheden en bronnen van de
          laatste zestig dagen, zonder selectie.
        </div>
        {alleGebeurtenissen && (
          <div style={{ marginTop: 12 }}>
            <EventFeed />
          </div>
        )}
      </div>

      {rol && (
        <>
          <Sectie
            titel={`Alleen voor ${rol.label}`}
            uitleg="Het blok dat deze rol van de andere twee onderscheidt."
          >
            {PLAN_ROLLEN.includes(rol.key) && stand.stand === "ingelogd" && magPlan(stand.mij) ? (
              <PlanKaart
                onOpen={() => onOpen?.({ soort: "tab", tab: "plan" })}
                sectorLabel={stand.mij.startSectorLabel}
              />
            ) : (
              <ComingSoon
                title={ROL_BLOK[rol.key]?.titel ?? rol.label}
                reason={
                  ROL_BLOK[rol.key]?.reden ??
                  "Deze rol is nieuw in de configuratie en heeft nog geen eigen blok."
                }
              />
            )}
          </Sectie>

          <Sectie
            titel="Jouw krediet"
            uitleg="Wat je per maand mag aanvragen, en hoeveel daarvan gebruikt is."
          >
            <Card style={{ padding: "16px 18px" }}>
              <Krediet rol={rol} stand={stand} isEigenRol={isEigenRol} />
            </Card>
          </Sectie>

          <Sectie titel="Propositie en sectoren">
            <Card style={{ padding: "16px 18px" }}>
              <SectionLabel>Waar je vanuit werkt</SectionLabel>
              <Propositie rol={rol} stand={stand} isEigenRol={isEigenRol} meta={meta} />
            </Card>
          </Sectie>
        </>
      )}

      {aanbouw && <InAanbouwPaneel item={aanbouw} onSluit={() => setAanbouw(null)} />}
    </div>
  );
}
